import React, { useCallback } from 'react';
import { matchPath, NavLink, useLocation } from 'react-router-dom';
import cx from 'classnames';
import { transformNavigationLink } from 'helpers/customNavigationRules';
import PropTypes from 'prop-types';
import qs from 'query-string';

import { useApplicationState } from '@atlas/core';
import { staticRoutes } from '@atlas/routing';
import { Label } from '@atlas/ui';

import useSignInRedirects from 'components/Auth/useSignInRedirects';
import { urlCanParse } from 'src/helpers/urlCanParse';

const HeaderMenuItem = ({ authRequired = false, className, label, link }) => {
  const { redirectToSignIn } = useSignInRedirects();
  const { authenticated } = useApplicationState();
  const { pathname, search } = useLocation();

  const realLink = transformNavigationLink(link);

  const redirectPath = React.useCallback(() => {
    if (authRequired && !authenticated) {
      return `${staticRoutes.signin}?path=signIn`;
    }

    return realLink;
  }, [realLink, authRequired, authenticated]);

  const itemClassName = cx('simple-menu-link', className);

  const active = !!matchPath(pathname, { path: realLink });

  const parsedQuery = qs.parse(search);
  const isDetailPage = !!parsedQuery.e;
  const isResultDetailPage = !!parsedQuery.r;
  const isSearchPage = !!parsedQuery.q;
  const onClickHandler = useCallback(
    e => {
      const isNeedAuth = authRequired && !authenticated;
      if (isNeedAuth || (active && !isDetailPage && !isResultDetailPage && !isSearchPage)) {
        e.preventDefault();
        if (isNeedAuth) redirectToSignIn({ redirectPath: realLink });
      }
    },
    [
      active,
      authRequired,
      authenticated,
      isDetailPage,
      isResultDetailPage,
      isSearchPage,
      realLink,
      redirectToSignIn,
    ],
  );

  return (
    <div className="simple-menu-item">
      {urlCanParse(link) ? (
        <a className={itemClassName} href={link} rel="noreferrer" target="_blank">
          <Label message={label} />
        </a>
      ) : (
        <NavLink
          activeClassName="active"
          className={itemClassName}
          exact={false}
          onClick={onClickHandler}
          strict={false}
          to={redirectPath}
        >
          <Label message={label} />
        </NavLink>
      )}
    </div>
  );
};

export default React.memo(HeaderMenuItem);

HeaderMenuItem.propTypes = {
  authRequired: PropTypes.bool,
  className: PropTypes.string,
  label: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
};
